import QRCode from 'qrcode';
import { supabase } from './supabase';
import { isNativeApp } from './nativeShell';

/**
 * Production web URL for the app.
 *
 * When we're running inside the iOS/Android Capacitor shell,
 * `window.location.origin` returns `capacitor://localhost` (the
 * internal WebView scheme) — a URL that no external phone can open.
 * Public sign-in QR codes must therefore hardcode the prod https URL
 * so subbies scanning the QR from a separate device land on the real
 * public sign-in page.
 */
const PROD_APP_ORIGIN = 'https://app.safeandsorted.com';

export interface PublicShareToken {
  id: string;
  token: string;
  purpose: 'site_signin' | 'rams_upload';
  org_id: string;
  site_id: string | null;
  subcontractor_id: string | null;
  active: boolean;
  expires_at: string;
  max_uses: number | null;
  use_count: number;
  created_by: string | null;
  created_at: string;
}

/** Generate a short cryptographically-random token (URL-safe base32). */
function genToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // base32 alphabet
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += alphabet[bytes[i] & 0x1f];
  }
  return out;
}

/** Create a public sign-in token for a site. */
export async function createSiteSigninToken(input: {
  orgId: string;
  siteId: string;
}): Promise<PublicShareToken> {
  const token = genToken();
  const { data, error } = await supabase
    .from('public_share_tokens')
    .insert({
      token,
      purpose: 'site_signin',
      org_id: input.orgId,
      site_id: input.siteId,
    })
    .select()
    .single();
  if (error) throw error;
  return data as PublicShareToken;
}

/** List public tokens for a site (includes inactive). */
export async function listSiteTokens(siteId: string): Promise<PublicShareToken[]> {
  const { data, error } = await supabase
    .from('public_share_tokens')
    .select('*')
    .eq('site_id', siteId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as PublicShareToken[];
}

/** Revoke a token (set active=false). */
export async function revokeToken(id: string): Promise<void> {
  const { error } = await supabase
    .from('public_share_tokens')
    .update({ active: false })
    .eq('id', id);
  if (error) throw error;
}

/** Validate a token. Returns site/org context when valid; throws on invalid. */
export async function validatePublicToken(token: string, purpose: 'site_signin' | 'rams_upload') {
  const { data, error } = await supabase.rpc('validate_public_token', {
    p_token: token,
    p_purpose: purpose,
  });
  if (error) throw error;
  if (!data || !data[0]) throw new Error('Token not valid');
  const r = data[0] as { org_id: string; org_name: string; site_id: string | null; site_name: string; expires_at: string };
  return r;
}

/** Submit a public sign-in via the RPC. */
export async function publicCreateSignin(input: {
  token: string;
  fullName: string;
  company: string;
  tradeRole?: string | null;
  mobile?: string | null;
  vehicleReg?: string | null;
  confirmations: boolean[];
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  accuracyM?: number | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('public_signin_create', {
    p_token: input.token,
    p_full_name: input.fullName,
    p_company: input.company,
    p_trade_role: input.tradeRole ?? null,
    p_mobile: input.mobile ?? null,
    p_vehicle_reg: input.vehicleReg ?? null,
    p_confirmations: input.confirmations,
    p_lat: input.lat ?? null,
    p_lng: input.lng ?? null,
    p_address: input.address ?? null,
    p_accuracy_m: input.accuracyM ?? null,
  });
  if (error) throw error;
  return data as string;
}

/** Public-facing URL for a sign-in token.
 *
 * Uses the hardcoded prod origin whenever we're inside the native shell
 * (see PROD_APP_ORIGIN comment above). On the web PWA it falls back to
 * `window.location.origin` so preview/staging deploys keep working. */
export function publicSigninUrl(token: string): string {
  const base = isNativeApp() ? PROD_APP_ORIGIN : window.location.origin;
  return `${base}/public/sign-in/${token}`;
}

/**
 * Generate a QR code as an inline data: URL.
 *
 * Generated locally via the `qrcode` package — no network call, so:
 *   • the sign-in token never leaves the browser (was previously sent
 *     to api.qrserver.com on every render);
 *   • works offline (cabin tablets often have flaky 4G);
 *   • zero dependency on a third-party service being up.
 *
 * Returns a Promise — the QR encoder is async-only on its public API.
 */
export async function qrCodeDataUrl(text: string, sizePx = 240): Promise<string> {
  return QRCode.toDataURL(text, {
    width: sizePx,
    margin: 1,
    color: { dark: '#0F172A', light: '#FFFFFF' },
    errorCorrectionLevel: 'M',
  });
}
